"use client";

/**
 * Блок «Популярные автомобили» — карточки классов авто. Данные берутся из
 * автопарка (классы) + настроек админки (название/картинка/порядок) через
 * SearchProvider: цена «от», места и багаж — по текущему набору (общий автопарк
 * или выбранный город). Карточка работает как фильтр: клик — показать в каталоге
 * только этот класс, повторный клик — сбросить.
 */

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import CarouselArrow from "./CarouselArrow";
import { useSearch } from "./SearchProvider";
import { useLocale } from "@/lib/locale-context";
import { carSpecLabel } from "@/lib/car-spec-i18n";
import { track } from "@/lib/metrika";

export default function PopularCars() {
  const { classes, activeClass, setClass, activeCity } = useSearch();
  const { locale } = useLocale();
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 1);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    update();
    const el = trackRef.current;
    el?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update, classes.length]);

  const scrollBy = (delta: number) => {
    trackRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  };

  if (!classes.length) return null;

  return (
    <div className="relative">
      <div
        ref={trackRef}
        className="flex snap-x gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {classes.map((t) => {
          const active = t.key === activeClass;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => { if (!active) track("class_filter", { city: activeCity?.slug }); setClass(active ? null : t.key); }}
              aria-pressed={active}
              className={`group flex w-[200px] shrink-0 snap-start cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 p-2 transition ${
                active
                  ? "border-primary bg-primary/[0.06] shadow-[var(--shadow-card)]"
                  : "border-transparent bg-white hover:shadow-[var(--shadow-card)]"
              }`}
            >
              <div className="flex w-[145px] flex-col items-center gap-1">
                <span className="text-[16px] leading-6 text-black">{carSpecLabel(t.label, locale)}</span>
                <div className="flex gap-3 text-[16px] leading-6 text-text-muted">
                  <span className="flex items-center gap-1" title={locale === "en" ? "Seats" : "Мест в салоне"}>
                    <span className="relative size-3.5">
                      <Image src="/icons/seats.svg" alt={locale === "en" ? "seats" : "мест"} fill sizes="14px" />
                    </span>
                    {t.seats}
                  </span>
                  <span className="flex items-center gap-1" title={locale === "en" ? "Luggage" : "Мест для багажа"}>
                    <span className="relative size-3.5">
                      <Image src="/icons/baggage.svg" alt={locale === "en" ? "luggage" : "багаж"} fill sizes="14px" />
                    </span>
                    {t.bags}
                  </span>
                </div>
              </div>
              <div className="relative h-[84px] w-full">
                {t.image ? (
                  <Image
                    src={t.image}
                    alt={carSpecLabel(t.label, locale)}
                    fill
                    className="object-contain transition-transform duration-300 group-hover:scale-[1.05]"
                    sizes="184px"
                  />
                ) : null}
              </div>
              <div className="flex w-full items-center justify-center gap-1 text-[16px] font-bold">
                <span className="text-text-muted">{locale === "en" ? "from" : "от"}</span>
                <span className="text-black">{t.priceFrom}</span>
              </div>
            </button>
          );
        })}
      </div>

      {canLeft && (
        <CarouselArrow
          direction="left"
          ariaLabel={locale === "en" ? "Back" : "Назад"}
          onClick={() => scrollBy(-220)}
          className="!rounded-full absolute left-0 top-1/2 z-10 -translate-y-1/2 shadow-[0_8px_24px_rgba(20,29,69,0.18)]"
        />
      )}
      {canRight && (
        <CarouselArrow
          direction="right"
          ariaLabel={locale === "en" ? "Next" : "Вперед"}
          onClick={() => scrollBy(220)}
          className="!rounded-full absolute right-0 top-1/2 z-10 -translate-y-1/2 shadow-[0_8px_24px_rgba(20,29,69,0.18)]"
        />
      )}
    </div>
  );
}
